"use client";

/**
 * Opportunity History pane (intake) — the design's "Phased accordion"
 * view. Heading + stat line, one HistoryVersionCard per saved version
 * (newest first, newest open), then the HistorySetupCard. Purely
 * presentational: apps/web fetches and maps the API contract into
 * `OpportunityHistoryVM` (see OpportunityHistoryTab).
 */

import { HistorySetupCard } from "./HistorySetupCard";
import { HistoryStatLine } from "./HistoryStatLine";
import { HistoryVersionCard } from "./HistoryVersionCard";
import type { OpportunityHistoryVM } from "./history-types";

export interface OpportunityHistoryProps {
  history: OpportunityHistoryVM;
  /** Wires every assumption-edit kebab's "Open in assumptions" action. */
  onOpenAssumptions?: () => void;
}

export function OpportunityHistory({
  history,
  onOpenAssumptions,
}: OpportunityHistoryProps) {
  return (
    <div className="mx-auto max-w-[860px] px-6 py-6">
      <div className="mb-1 text-[11px] font-semibold tracking-[.12em] text-(--fg-muted) uppercase">
        Report · History
      </div>
      <h1 className="mb-1 font-[family-name:var(--font-display)] text-[24px] font-bold tracking-tight text-(--fg-primary)">
        Opportunity history
      </h1>
      <p className="mb-5 max-w-[640px] text-[13px] leading-relaxed text-(--fg-secondary)">
        One system of record for every edit to this opportunity, when it
        happened, and who made it.
      </p>
      <HistoryStatLine
        changeCount={history.changeCount}
        contributorNames={history.contributorNames}
        lastChangeAt={history.lastChangeAt}
      />
      {history.versions.length === 0 ? (
        <p className="mb-3 text-[13px] text-(--fg-muted)">
          No changes since initial setup.
        </p>
      ) : null}
      {history.versions.map((version, index) => (
        <HistoryVersionCard
          key={`${version.versionNumber}-${index}`}
          version={version}
          defaultOpen={index === 0}
          onOpenAssumptions={onOpenAssumptions}
        />
      ))}
      <HistorySetupCard setup={history.setup} />
    </div>
  );
}
